import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import { OrganizationInvitation } from "@Modules/teams/Data/Models/OrganizationInvitationModel";

export type OrganizationInvitationsApi = {
    invitations: ComputedRef<OrganizationInvitation[]>;
    loading: ComputedRef<boolean>;
    fetchInvitations: () => Promise<void>;
    acceptInvitation: (id: string) => Promise<void>;
    rejectInvitation: (id: string) => Promise<void>;
    reset: () => void;
    setRouter: (router: Pick<HttpInterface, "get" | "post">) => OrganizationInvitationsApi;
}

export default function OrganizationInvitationsApi(): OrganizationInvitationsApi {
    const state = reactive<{
        invitations: OrganizationInvitation[];
        loading: boolean;
    }>({
        invitations: [],
        loading: false,
    });

    let router: Pick<HttpInterface, "get" | "post"> = axios;

    const that = {
        invitations: computed(() => state.invitations),
        loading: computed(() => state.loading),

        fetchInvitations: async function (): Promise<void> {
            state.loading = true;
            const response = await router.get("/app/api/invitations/organization") as HttpResponse<OrganizationInvitation[]>;
            state.invitations = response.data;
            state.loading = false;
        },

        acceptInvitation: async function (id: string): Promise<void> {
            const invitation = state.invitations.find(inv => inv.id === id);
            if (!invitation) {
                throw new Error(`Invitation ${id} not found`);
            }
            const orgSlug = invitation.organization.slug;
            const response = await router.post(`/app/api/organization/${orgSlug}/users/invite/${id}/accept`, {});
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not accept invitation ${id}`);
            }
            state.invitations = state.invitations.filter(inv => inv.id !== id);
        },

        rejectInvitation: async function (id: string): Promise<void> {
            const invitation = state.invitations.find(inv => inv.id === id);
            if (!invitation) {
                throw new Error(`Invitation ${id} not found`);
            }
            const orgSlug = invitation.organization.slug;
            const response = await router.post(`/app/api/organization/${orgSlug}/users/invite/${id}/reject`, {});
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not reject invitation ${id}`);
            }
            state.invitations = state.invitations.filter(inv => inv.id !== id);
        },

        reset: function (): void {
            state.invitations = [];
            state.loading = false;
        },

        setRouter: function (newRouter: Pick<HttpInterface, "get" | "post">) {
            router = newRouter;
            return that;
        },
    };

    return that;
}
